import { AnimatedContentLoader } from '@root/components'
import { useBusinessContext } from '@root/context'
import { fetchReceipts } from '@root/query'
import { formatCurrency, formatDate } from '@root/utils'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FaExternalLinkAlt } from 'react-icons/fa'
import { useQuery } from '@tanstack/react-query'
import styled from 'styled-components'

export const ReceiptTable: React.FC = () => {
  const { slug, data } = useBusinessContext()
  const { t } = useTranslation()

  const { data: receipts, isLoading: isReceiptsLoading } = useQuery({
    queryKey: [slug, 'stripe-receipts'],
    queryFn: () => fetchReceipts({ slug }),
    ...{
      enabled: !!data?.stripe_subscription_id
    }
  })
  const [showAll, setShowAll] = useState(false)
  const displayedReceipts = showAll ? receipts : receipts?.slice(0, 5)
  const hasMoreReceipts = receipts && receipts.length > 5

  return (
    <div>
      <AnimatedContentLoader
        isLoading={isReceiptsLoading}
        isEmpty={!receipts?.length}
        isEmptyContent={
          <EmptyContent>
            <p>{t('settings.billing.noReceipts')}</p>
          </EmptyContent>
        }
      >
        <ReceiptList>
          {displayedReceipts?.map(receipt => (
            <ReceiptItem key={receipt.id}>
              <span>{formatDate(receipt.created)}</span>
              <DownloadSection>
                <span>{formatCurrency(receipt.amount, null, receipt.currency)}</span>
                <a href={receipt.receipt_url} target="_blank" rel="noopener noreferrer">
                  <span>{t('settings.billing.openReceipt')}</span>
                  <FaExternalLinkAlt />
                </a>
              </DownloadSection>
            </ReceiptItem>
          ))}
          {hasMoreReceipts && !showAll && (
            <ShowAllLink
              href="#"
              onClick={e => {
                e.preventDefault()
                setShowAll(true)
              }}
            >
              {t('settings.billing.showAll')}
            </ShowAllLink>
          )}
        </ReceiptList>
      </AnimatedContentLoader>
    </div>
  )
}

const ReceiptList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xxs}rem;
  font-size: ${({ theme }) => theme.fontSize.md}rem;
`

const ShowAllLink = styled.a`
  color: ${({ theme }) => theme.colors.primary};
  font-size: ${({ theme }) => theme.fontSize.md}rem;
  padding: ${({ theme }) => theme.spacing.sm}rem;
  text-align: right;
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs}rem;
  justify-content: flex-end;
`

const ReceiptItem = styled.li`
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: ${({ theme }) => theme.colors.metalGray};
  padding: ${({ theme }) => theme.spacing.sm}rem;

  &:not(:last-of-type) {
    border-bottom: 1px solid ${({ theme }) => theme.colors.neutralGray};
  }

  a {
    color: ${({ theme }) => theme.colors.primary};
    display: flex;
    align-items: center;
    gap: ${({ theme }) => theme.spacing.xs}rem;
    font-size: ${({ theme }) => theme.fontSize.md}rem;
  }
`

const DownloadSection = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md}rem;
`

const EmptyContent = styled.div`
  font-size: ${({ theme }) => theme.fontSize.md}rem;
  color: ${({ theme }) => theme.colors.metalGray};
`
